import { ResolutionModel, ZipInitBatch, ZipInitResponse } from '@aipb/gulliver/dropbox/model';
import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { Observable } from 'rxjs';

export interface MultiImageDownloadChecked {
  id: string;
  image: string;
  type: string;
  title: string;
}

interface MultiImageDownloadProps {
  active: boolean;
  checked: MultiImageDownloadChecked[];
  batches: ZipInitBatch;
  resolutions: ResolutionModel[];
  selected: MultiImageDownloadChecked;
  downloadInProgress: boolean;
}

const store = createStore(
  { name: 'catalog-multi-image-download' },
  withProps<MultiImageDownloadProps>({
    active: false,
    checked: [],
    batches: {
      allowed: true,
      maxConcurrent: 1,
      maxSize: 0
    },
    resolutions: [],
    selected: null,
    downloadInProgress: false
  })
);

@Injectable({
  providedIn: 'root'
})
export class MultiImageDownloadRepository {
  active$ = store.pipe(select((state) => state.active));
  countChecked$ = store.pipe(select((state) => state.checked.length));
  maxBatchSize$ = store.pipe(select((state) => state.batches.maxSize));
  resolutions$ = store.pipe(select((state) => state.resolutions));
  canToggleChecked$ = store.pipe(
    select((state) => {
      const maxBatchSize = state.batches.maxSize;
      const countChecked = state.checked.length;

      return countChecked < maxBatchSize;
    })
  );
  checked$ = store.pipe(select((state) => state.checked));
  selected$ = store.pipe(select((state) => state.selected));
  downloadInProgress$ = store.pipe(select((state) => state.downloadInProgress));

  get active() {
    return store.getValue().active;
  }

  set active(active: boolean) {
    store.update((state) => ({
      ...state,
      active
    }));
  }

  set downloadInProgress(downloadInProgress: boolean) {
    store.update((state) => ({
      ...state,
      downloadInProgress
    }));
  }

  get countChecked(): number {
    return this.getChecked().length;
  }

  get resolutions(): ResolutionModel[] {
    return store.getValue().resolutions;
  }

  get selected() {
    return store.getValue().selected;
  }

  init(data: ZipInitResponse) {
    store.update((state) => ({
      ...state,
      batches: data.batches,
      resolutions: data.resAvailable
    }));
  }

  toggleChecked(data: MultiImageDownloadChecked): void {
    store.update((state) => {
      let checked: MultiImageDownloadChecked[] = state.checked;
      let selected = state.selected;

      if (state.checked.some((item) => item.id === data.id)) {
        checked = state.checked.filter((k) => k.id !== data.id);
        if (state.selected && state.selected.id === data.id) {
          selected = null;
        }
      } else {
        if (state.checked.length >= state.batches.maxSize) return state;

        checked = [...state.checked, data];
      }

      return {
        ...state,
        checked,
        selected
      };
    });
  }

  addChecked(data: MultiImageDownloadChecked): void {
    store.update((state) => {
      let checked: MultiImageDownloadChecked[] = state.checked;
      const selected = state.selected;

      if (!state.checked.some((item) => item.id === data.id)) {
        if (state.checked.length >= state.batches.maxSize) return state;

        checked = [...state.checked, data];
      }

      return {
        ...state,
        checked,
        selected
      };
    });
  }

  toggleActive(): void {
    store.update((state) => ({
      ...state,
      active: !state.active
    }));
  }

  setSelected(selected: MultiImageDownloadChecked): void {
    store.update((state) => ({
      ...state,
      selected
    }));
  }

  uncheck(data: MultiImageDownloadChecked): void {
    store.update((state) => ({
      ...state,
      checked: state.checked.filter((k) => k.id !== data.id),
      selected: state.selected && state.selected.id === data.id ? null : state.selected
    }));
  }

  cancelSelection(): void {
    store.update((state) => ({
      ...state,
      checked: [],
      selected: null
    }));
  }

  selectChecked(data: MultiImageDownloadChecked): Observable<boolean> {
    return store.pipe(
      select((state) => {
        return state.checked.some((item) => item.id === data.id);
      })
    );
  }

  setChecked(checked: MultiImageDownloadChecked[]) {
    store.update((state) => {
      if (checked.length > state.batches.maxSize) return state;

      return {
        ...state,
        checked
      };
    });
  }

  getChecked(): MultiImageDownloadChecked[] {
    return store.getValue().checked;
  }

  reset() {
    store.reset();
  }
}
